import { defineStore } from 'pinia';
import { computed, ref, type Ref } from 'vue';
import { API } from '@vp/common';
import { useConnectionStore } from '@/stores/connection';
import { useRootStore } from '@/stores/root';

/**
 * The signed-in operator's own account: its id, its config and its enabled features.
 *
 * Tenant-facing and instantiated app-wide — by `useFeatures`, both app roots, the order index,
 * venue edit and more. That is why the superadmin account administration (cross-account reads and
 * writes on the internal VP-staff schema) lives in `useSuperadminAccountStore` instead: reading
 * `internalGqlApiClient` here would put a staff-only connection in every page's setup path.
 */
export const useAccountStore = defineStore('account', () => {
  const connectionStore = useConnectionStore();
  const rootStore = useRootStore();
  const gqlApiClient = connectionStore.gqlApiClient!;

  const accountConfig: Ref<API.App.User.AccountConfig.AccountConfig | undefined> = ref();

  const loadAccountConfig = async () => {
    accountConfig.value = await API.App.User.Api.fetchAccountConfig(gqlApiClient);
  };

  /**
   * AppSchema resolves only the *enabled* features and gives them no `enabled` flag, so the
   * filter that used to sit here has no field left to read — every entry is an enabled one.
   */
  const features = computed(() => (accountConfig.value?.features ?? []).map(({ name }) => name));

  /**
   * The account every scoped read is against. AppSchema carries no id on the config — the account
   * is implied by the request — so it comes from the connection store, which is now written in
   * both modes: `main.common.ts` seeds it from `window.vpAppConfig.currentAccountId` when
   * embedded, and `AppStandalone` resolves it from the switcher's selection in standalone.
   *
   * The root store fallback is a belt-and-braces read of the same host value the seed uses, kept
   * so this cannot resolve to nothing on a page that mounts outside the app roots. Its `0` means
   * "no host config", not account zero, hence `|| undefined` — a scoped read omits the argument
   * rather than filtering on an account that cannot exist.
   *
   * `accountConfig` and this are sourced independently but no longer disagree: `AppStandalone`
   * assigns `connectionStore.accountId` *before* it awaits `loadAccountConfig()`, so that fetch
   * carries the header and resolves the same account this names. It used to run the other way
   * round, which is how a switcher selection and the features it gated could describe two
   * different tenants (sc-19910).
   */
  const accountId = computed(() => connectionStore.accountId ?? (rootStore.currentAccountId || undefined));

  return {
    accountConfig,
    accountId,
    loadAccountConfig,
    features,
  };
});
